import {groupRosterPlayers} from "@/utils/rosterPlayerGroups";
import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import ChoiceChips from "@/components/ChoiceChips";
import StatusBadge from "@/components/StatusBadge";
import Tabs from "@/components/Tabs";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import EmptyData from "@/components/EmptyData";
import VLFModal from "@/components/Modal";
import RosterLineup from "@/components/RosterLineup";

import Checkbox from "@mui/material/Checkbox";
import Pagination from "@mui/material/Pagination";
import AddIcon from "@mui/icons-material/Add";
import ArrowBack from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

import {ModuleAPI} from "@/api/module";
import {PlayerAPI} from "@/api/player";
import {PlayerRoleAPI} from "@/api/player_role";
import {RosterAPI} from "@/api/roster";
import {StaffAPI} from "@/api/staff";

import {
	AREA_OPTIONS,
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {
	buildPlayerBody,
	checkRequiredFields,
	getBirthYears,
	getPlayerDetailPath,
} from "@/utils/functions";
import {useStaffRoles} from "@/utils/hooks";

import "./index.scss";

const DB_PAGE_SIZE = 25;

const ROSA_VIEW_OPTIONS = [
	{value: "field", label: "Campo"},
	{value: "list", label: "Lista"},
];

const ADD_TAB_OPTIONS = [
	{value: "database", label: "Dall'archivio"},
	{value: "new", label: "Crea nuovo"},
];

const ScoutingRosterPage = (props) => {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [roster, setRoster] = useState();
	const [currentTab, setCurrentTab] = useState("main_info");
	const [rosaView, setRosaView] = useState("field");
	const lineupRef = useRef(null);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);

	// Moduli
	const [allModules, setAllModules] = useState([]);
	const [selectedModuleToAdd, setSelectedModuleToAdd] = useState(null);

	// Modal quick-add
	const [addModal, setAddModal] = useState(null); // null | "players" | "staffs"
	const [addTab, setAddTab] = useState("database"); // "database" | "new"
	const [addSearch, setAddSearch] = useState("");
	const [addRoleFilter, setAddRoleFilter] = useState(null);
	const [addYearFilter, setAddYearFilter] = useState(null);
	const [dbResults, setDbResults] = useState([]);
	const [dbPage, setDbPage] = useState(1);
	const [dbTotalItems, setDbTotalItems] = useState(0);
	const [processingIds, setProcessingIds] = useState(new Set());
	const [newFields, setNewFields] = useState({});
	const [newFieldsErrors, setNewFieldsErrors] = useState([]);
	const [addLoading, setAddLoading] = useState(false);
	const [staffUnits, setStaffUnits] = useState([]);
	const [playerRolesList, setPlayerRolesList] = useState([]);

	const {staffRoles} = useStaffRoles({setLoading: false, handleNotification});
	const searchDebounceRef = useRef(null);

	// ── Fetch dati ───────────────────────────────────────────────────────────────

	const getRoster = async () => {
		setLoading(true);
		try {
			const response = await RosterAPI.getById(id);
			setRoster(response);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getAllModules = async () => {
		try {
			const response = await ModuleAPI.list();
			setAllModules(response);
		} catch {
			// silenzioso
		}
	};

	const getStaffUnits = async () => {
		try {
			const response = await StaffAPI.unitsList();
			setStaffUnits(response);
		} catch {
			// silenzioso
		}
	};

	const loadPlayerRoles = async () => {
		try {
			const response = await PlayerRoleAPI.list();
			setPlayerRolesList(response || []);
		} catch {
			// silenzioso
		}
	};

	useEffect(() => {
		getRoster();
		getAllModules();
		getStaffUnits();
		loadPlayerRoles();
	}, []);

	// ── Gestione moduli ──────────────────────────────────────────────────────────

	const assignedModuleIds = new Set(
		(roster?.modules || []).map((m) => m.moduleId),
	);
	const availableModuleOptions = allModules
		.filter((m) => !assignedModuleIds.has(m.id))
		.map((m) => ({value: m.id, label: m.name}));

	const handleAddModule = async () => {
		if (!selectedModuleToAdd) return;
		setLoading(true);
		try {
			await RosterAPI.addModule({
				rosterId: id,
				moduleId: selectedModuleToAdd.value,
			});
			setSelectedModuleToAdd(null);
			await getRoster();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleRemoveModule = async (moduleId) => {
		setLoading(true);
		try {
			await RosterAPI.deleteModule(id, moduleId);
			await getRoster();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleSetDefaultModule = async (moduleId) => {
		setLoading(true);
		try {
			await RosterAPI.setDefaultModule(id, moduleId);
			await getRoster();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	// ── Rimozione inline ─────────────────────────────────────────────────────────

	const handleRemovePlayer = async (player) => {
		setLoading(true);
		try {
			await RosterAPI.deletePlayer(id, player.player.id);
			await getRoster();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleRemoveStaff = async (staffMember) => {
		setLoading(true);
		try {
			await RosterAPI.deleteStaff(id, staffMember.staff.id);
			await getRoster();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	// ── Modal quick-add ──────────────────────────────────────────────────────────

	const openAddModal = (type) => {
		setAddModal(type);
		setAddTab("database");
		setAddSearch("");
		setAddRoleFilter(null);
		setAddYearFilter(null);
		setNewFields({});
		setNewFieldsErrors([]);
		setDbResults([]);
		setDbPage(1);
		setDbTotalItems(0);
	};

	const closeAddModal = () => {
		setAddModal(null);
		setDbResults([]);
	};

	const loadDbPlayers = useCallback(async (search, role, year, page = 1) => {
		setAddLoading(true);
		try {
			const response = await PlayerAPI.paginated({
				page,
				pageSize: DB_PAGE_SIZE,
				search: search || undefined,
				role: role?.label || undefined,
				birthYear: year?.value || undefined,
			});
			setDbResults(response.data || []);
			setDbTotalItems(response.totalRecords || 0);
		} catch {
			// silenzioso
		} finally {
			setAddLoading(false);
		}
	}, []);

	const loadDbStaff = useCallback(async (search) => {
		setAddLoading(true);
		try {
			const response = await StaffAPI.list();
			const filtered = (
				search
					? response.filter((s) =>
							(s.lastName + " " + (s.firstName || ""))
								.toLowerCase()
								.includes(search.toLowerCase()),
						)
					: response
			).sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
			setDbResults(filtered);
		} catch {
			// silenzioso
		} finally {
			setAddLoading(false);
		}
	}, []);

	// caricamento iniziale quando si apre la modale
	useEffect(() => {
		if (addModal === "players") loadDbPlayers("", null, null, 1);
		else if (addModal === "staffs") loadDbStaff("");
	}, [addModal, loadDbPlayers, loadDbStaff]);

	// ricerca con debounce (search + filtri resettano la pagina a 1)
	useEffect(() => {
		if (!addModal) return;
		if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		searchDebounceRef.current = setTimeout(() => {
			if (addModal === "players")
				loadDbPlayers(addSearch, addRoleFilter, addYearFilter, 1);
			else loadDbStaff(addSearch);
			setDbPage(1);
		}, 400);
		return () => clearTimeout(searchDebounceRef.current);
	}, [
		addSearch,
		addRoleFilter,
		addYearFilter,
		addModal,
		loadDbPlayers,
		loadDbStaff,
	]);

	// cambio pagina (immediato, senza debounce)
	useEffect(() => {
		if (!addModal || addModal !== "players") return;
		loadDbPlayers(addSearch, addRoleFilter, addYearFilter, dbPage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dbPage]);

	const currentRosterPlayers = () =>
		(roster?.players || []).map((p) => ({
			rosterId: id,
			playerId: p.player.id,
			jerseyNumber: p.jerseyNumber || undefined,
		}));

	// Dal campo: chi viene schierato e non è in rosa entra nel roster.
	const addPlayerFromLineup = async () => {
		setRoster(await RosterAPI.getById(id));
	};

	const toggleRosterMembership = async (item, inRoster) => {
		setProcessingIds((prev) => new Set(prev).add(item.id));
		try {
			if (addModal === "players") {
				const currentPlayers = currentRosterPlayers();
				const newPlayers = inRoster
					? currentPlayers.filter((p) => p.playerId !== item.id)
					: [...currentPlayers, {rosterId: id, playerId: item.id}];
				await RosterAPI.update(id, {...roster, players: newPlayers});
			} else {
				const currentStaff = (roster?.staff || []).map((s) => ({
					rosterId: id,
					staffId: s.staff.id,
				}));
				const newStaff = inRoster
					? currentStaff.filter((s) => s.staffId !== item.id)
					: [...currentStaff, {rosterId: id, staffId: item.id}];
				await RosterAPI.update(id, {...roster, staff: newStaff});
			}
			await getRoster();
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setProcessingIds((prev) => {
				const next = new Set(prev);
				next.delete(item.id);
				return next;
			});
		}
	};

	const handleCreateNew = async () => {
		const isPlayers = addModal === "players";
		const required = isPlayers
			? ["lastName"]
			: ["lastName", "staffRole", "staffUnit"];
		const errors = checkRequiredFields(required, newFields);
		setNewFieldsErrors(errors);
		if (errors.length) return;

		setAddLoading(true);
		try {
			let created;
			const isGoalkeeper = newFields.primaryRole?.value === "Goalkeeper";
			if (isPlayers) {
				created = await PlayerAPI.create(
					buildPlayerBody({
						firstName: newFields.firstName,
						lastName: newFields.lastName,
						dateOfBirth: newFields.dateOfBirth,
						goalkeeper: isGoalkeeper,
						primaryRole: newFields.primaryRole?.label,
						secondaryRole: newFields.secondaryRole?.label,
					}),
				);
				await RosterAPI.update(id, {
					...roster,
					players: [
						...currentRosterPlayers(),
						{rosterId: id, playerId: created.id},
					],
				});
			} else {
				created = await StaffAPI.create({
					firstName: newFields.firstName || null,
					lastName: newFields.lastName,
					staffRoleId: newFields.staffRole.value,
					staffRole: {
						id: newFields.staffRole.value,
						name: newFields.staffRole.label,
					},
					staffUnitId: newFields.staffUnit.value,
					staffUnit: {
						id: newFields.staffUnit.value,
						name: newFields.staffUnit.label,
					},
				});
				const currentStaff = (roster?.staff || []).map((s) => ({
					rosterId: id,
					staffId: s.staff.id,
				}));
				await RosterAPI.update(id, {
					...roster,
					staff: [...currentStaff, {rosterId: id, staffId: created.id}],
				});
			}
			setNewFields({});
			setNewFieldsErrors([]);
			setAddTab("database");
			await getRoster();
			if (isPlayers) loadDbPlayers("", null, null);
			else loadDbStaff("");
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setAddLoading(false);
		}
	};

	// ── Elimina roster ───────────────────────────────────────────────────────────

	const deleteRoster = async () => {
		setLoading(true);
		try {
			await RosterAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/development/rosters");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const goBack = () => navigate("/development/rosters");

	// ── Configurazioni vista ─────────────────────────────────────────────────────

	const mainConfiguration = [
		{value: roster?.name, label: "Nome", size: "col-6 col-lg-3"},
		{value: roster?.season?.name, label: "Stagione", size: "col-6 col-lg-3"},
		// {value: roster?.playerCategory?.name, label: "Categoria", size: "col-6 col-lg-3"},
		{
			value: roster?.competitionSeasons?.length
				? roster.competitionSeasons
						.map(
							(cs) =>
								(cs.competition?.name || "") +
								(cs.season?.name ? ` (${cs.season.name})` : ""),
						)
						.join(", ")
				: "—",
			label: "Competizioni",
			size: "col-12 col-lg-3",
		},
	];

	const groupedPlayers = groupRosterPlayers(roster?.players || []);

	const STAFF_UNIT_PRIORITY = ["generale", "tecnica"];
	const groupedStaff = (() => {
		const groups = {};
		(roster?.staff || []).forEach((s) => {
			const unit = s.staff?.unit?.name || s.staff?.unit || "—";
			if (!groups[unit]) groups[unit] = [];
			groups[unit].push(s);
		});
		const ordered = {};
		STAFF_UNIT_PRIORITY.forEach((keyword) => {
			const key = Object.keys(groups).find((k) =>
				k.toLowerCase().includes(keyword),
			);
			if (key) ordered[key] = groups[key];
		});
		Object.keys(groups)
			.filter(
				(k) => !STAFF_UNIT_PRIORITY.some((kw) => k.toLowerCase().includes(kw)),
			)
			.sort()
			.forEach((k) => {
				ordered[k] = groups[k];
			});
		return ordered;
	})();

	const playersListConfiguration = {
		data: groupedPlayers,
		onClick: (player) => navigate(getPlayerDetailPath(player?.player)),
		handleDelete: handleRemovePlayer,
		separator: false,
	};

	const staffListConfiguration = {
		data: groupedStaff,
		onClick: (staff) =>
			!staff?.staff?.isInternal
				? navigate("/scouting/staffs/" + staff?.staff.id)
				: navigate("/manager/staffs/" + staff?.staff.id),
		handleDelete: handleRemoveStaff,
	};

	const modulesColumns = [
		{
			label: "Modulo",
			key: "module.name",
			align: "start",
			customRender: (item) => item?.module?.name ?? "—",
		},
		{
			label: "Difensori",
			key: "module.defenders",
			small: true,
			align: "center",
			customRender: (item) => item?.module?.defenders ?? "—",
		},
		{
			label: "Mediani",
			key: "module.defensiveMidfielders",
			small: true,
			align: "center",
			customRender: (item) => item?.module?.defensiveMidfielders ?? "—",
		},
		{
			label: "Centrocampisti",
			key: "module.centralMidfielders",
			small: true,
			align: "center",
			customRender: (item) => item?.module?.centralMidfielders ?? "—",
		},
		{
			label: "Trequartista",
			key: "module.attackingMidfielders",
			small: true,
			align: "center",
			customRender: (item) => item?.module?.attackingMidfielders ?? "—",
		},
		{
			label: "Attaccanti",
			key: "module.attackers",
			small: true,
			align: "center",
			customRender: (item) => item?.module?.attackers ?? "—",
		},
		{
			label: "Predefinito",
			key: "isDefault",
			small: true,
			align: "center",
			customRender: (item) =>
				item?.isDefault ? (
					<StarIcon fontSize="small" color="warning" />
				) : (
					<StarBorderIcon
						fontSize="small"
						className="scouting-roster-page__icon-action"
						onClick={() => handleSetDefaultModule(item.moduleId)}
					/>
				),
		},
		{
			label: "",
			key: "actions",
			small: true,
			align: "end",
			customRender: (item) => (
				<DeleteIcon
					fontSize="small"
					color="error"
					className="scouting-roster-page__icon-action"
					onClick={() => handleRemoveModule(item.moduleId)}
				/>
			),
		},
	];

	// ── Actions breadcrumbs ──────────────────────────────────────────────────────

	let mainActions = [];
	if (currentTab === "main_info") {
		mainActions = [
			{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
			{
				icon: <EditIcon />,
				title: "Modifica",
				onClick: () => navigate("/development/rosters/" + roster?.id + "/edit"),
			},
			{
				icon: <DeleteIcon />,
				title: "Elimina",
				onClick: () => setOpenHandleConfirm(true),
				color: "error",
			},
		];
	} else {
		mainActions = [
			{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		];
	}

	const breadcrumbsElements = [
		{label: "Rose", path: "/development/rosters"},
		{label: "/"},
	];

	// ── Tabs ─────────────────────────────────────────────────────────────────────

	const tabs = [
		{
			key: "main_info",
			label: "Informazioni",
			render: () => (
				<div className="row no-margin g-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
			),
		},
		{
			key: "players",
			label: "Giocatori",
			render: () => (
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<div className="scouting-roster-page__toolbar">
							<ChoiceChips
								options={ROSA_VIEW_OPTIONS}
								value={rosaView}
								onChange={setRosaView}
							/>
							<MainActions
								actions={[
									...(rosaView === "field"
										? [
												{
													icon: <PrintIcon />,
													label: "Stampa",
													title: "Stampa",
													onClick: () => lineupRef.current?.print(),
												},
											]
										: []),
									{
										icon: <AddIcon />,
										label: "Aggiungi",
										title: "Aggiungi giocatori",
										onClick: () => openAddModal("players"),
									},
								]}
							/>
						</div>
					</div>
					<div className="col-sm-12 padding-small">
						{rosaView === "field" ? (
							roster && (
								<RosterLineup
									ref={lineupRef}
									roster={roster}
									modules={allModules}
									onAddPlayer={addPlayerFromLineup}
									onOpenPlayer={(player) =>
										navigate(getPlayerDetailPath(player))
									}
									handleNotification={handleNotification}
								/>
							)
						) : (
							<CardsList
								type="roster_player"
								grouped
								breadcrumbs
								tabbed
								customFilters
								listConfiguration={playersListConfiguration}
								groupSorted={false}
							/>
						)}
					</div>
				</div>
			),
		},
		{
			key: "staffs",
			label: "Staff",
			render: () => (
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<div className="main-actions-bar">
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: "Aggiungi",
										title: "Aggiungi staff",
										onClick: () => openAddModal("staffs"),
									},
								]}
							/>
						</div>
					</div>
					<div className="col-sm-12 padding-small">
						<CardsList
							type="roster_staff"
							grouped
							groupSorted={false}
							breadcrumbs
							listConfiguration={staffListConfiguration}
							defaultSortedField="lastName"
						/>
					</div>
				</div>
			),
		},
		{
			key: "modules",
			label: "Moduli",
			render: () => (
				<div className="row no-margin g-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							openable={false}
							content={
								<div className="scouting-roster-page__module-add">
									<div className="scouting-roster-page__module-select">
										<VLFSelectNew
											options={availableModuleOptions}
											value={selectedModuleToAdd}
											onChange={(newValue) => setSelectedModuleToAdd(newValue)}
											label="Modulo"
											placeholder="Seleziona un modulo..."
											noMargin
										/>
									</div>
									<div>
										<MainActions
											actions={[
												{
													icon: <AddIcon />,
													label: "Aggiungi",
													title: "Aggiungi modulo",
													disabled: !selectedModuleToAdd,
													onClick: handleAddModule,
												},
											]}
										/>
									</div>
								</div>
							}
						/>
					</div>
					<div className="col-sm-12 padding-small">
						{roster?.modules?.length ? (
							<CardsTable
								configurationColumns={modulesColumns}
								data={roster.modules}
								fullHeight={false}
								middleHeight
							/>
						) : (
							<EmptyData>Nessun modulo assegnato</EmptyData>
						)}
					</div>
				</div>
			),
		},
	];

	// ── Render modale quick-add ──────────────────────────────────────────────────

	const isPlayers = addModal === "players";
	const alreadyInRosterIds = new Set(
		isPlayers
			? (roster?.players || []).map((p) => p.player?.id)
			: (roster?.staff || []).map((s) => s.staff?.id),
	);

	const renderDbItem = (item) => {
		const inRoster = alreadyInRosterIds.has(item.id);
		const processing = processingIds.has(item.id);
		const role = isPlayers
			? item.goalkeeper
				? "Portiere"
				: item.primaryRole
			: item.role;
		const detail = isPlayers
			? item.dateOfBirth && moment(item.dateOfBirth).format("YYYY")
			: item.unit;
		return (
			<div
				key={item.id}
				onClick={() => !processing && toggleRosterMembership(item, inRoster)}
				className={
					"roster-add-modal__item" +
					(inRoster ? " roster-add-modal__item--in-roster" : "") +
					(processing ? " roster-add-modal__item--processing" : "")
				}
			>
				<Checkbox
					checked={inRoster}
					disabled={processing}
					size="small"
					className="roster-add-modal__check"
					onClick={(e) => {
						e.stopPropagation();
						if (!processing) toggleRosterMembership(item, inRoster);
					}}
				/>
				<span className="roster-add-modal__name">
					{(item.firstName ? item.firstName + " " : "") + item.lastName}
				</span>
				<span className="roster-add-modal__meta">
					{role && <span className="roster-add-modal__role">{role}</span>}
					{detail && (
						<span className="roster-add-modal__detail">· {detail}</span>
					)}
					<StatusBadge
						tone={item.isInternal ? "good" : "neutral"}
						label={item.isInternal ? "Interno" : "Scouting"}
					/>
				</span>
			</div>
		);
	};

	const addModalBody = (
		<div className="roster-add-modal__body">
			<ChoiceChips
				options={ADD_TAB_OPTIONS}
				value={addTab}
				onChange={setAddTab}
			/>
			{addTab === "database" ? (
				<>
					<div className="roster-add-modal__filters">
						<div className="roster-add-modal__filter roster-add-modal__filter--search">
							<VLFTextField
								name="search"
								value={addSearch}
								onChange={(e) => setAddSearch(e.target.value)}
								label={isPlayers ? "Giocatore" : "Staff"}
								placeholder={
									isPlayers ? "Cerca giocatore..." : "Cerca staff..."
								}
								size="small"
							/>
						</div>
						{isPlayers && (
							<>
								<div className="roster-add-modal__filter">
									<VLFSelectNew
										options={AREA_OPTIONS}
										sorted={false}
										value={addRoleFilter}
										onChange={(v) => setAddRoleFilter(v)}
										label="Ruolo"
										isClearable
										noMargin
									/>
								</div>
								<div className="roster-add-modal__filter">
									<VLFSelectNew
										options={getBirthYears()}
										sorted={false}
										value={addYearFilter}
										onChange={(v) => setAddYearFilter(v)}
										label="Anno"
										isClearable
										noMargin
									/>
								</div>
							</>
						)}
					</div>

					{addLoading ? (
						<div className="roster-add-modal__message">Caricamento...</div>
					) : dbResults.length === 0 ? (
						<EmptyData>Nessun risultato</EmptyData>
					) : (
						<div className="roster-add-modal__list">
							{dbResults.map(renderDbItem)}
						</div>
					)}
					{isPlayers && dbTotalItems > DB_PAGE_SIZE && (
						<div className="roster-add-modal__pagination">
							<Pagination
								count={Math.ceil(dbTotalItems / DB_PAGE_SIZE)}
								page={dbPage}
								onChange={(_, page) => setDbPage(page)}
								size="small"
								color="primary"
							/>
						</div>
					)}
				</>
			) : (
				<div className="roster-add-modal__form">
					<VLFTextField
						name="firstName"
						value={newFields.firstName || ""}
						onChange={(e) =>
							setNewFields({...newFields, firstName: e.target.value})
						}
						label="Nome"
						size="small"
						onlyLabel
					/>
					<VLFTextField
						name="lastName"
						value={newFields.lastName || ""}
						onChange={(e) => {
							setNewFieldsErrors([]);
							setNewFields({...newFields, lastName: e.target.value});
						}}
						label="Cognome"
						size="small"
						required
						onlyLabel
						error={newFieldsErrors.includes("lastName")}
						helperText={
							newFieldsErrors.includes("lastName") ? REQUIRED_FIELD : ""
						}
					/>
					{isPlayers && (
						<>
							<VLFSelectNew
								options={AREA_OPTIONS}
								sorted={false}
								value={newFields.primaryRole}
								onChange={(v) =>
									setNewFields({
										...newFields,
										primaryRole: v,
										secondaryRole: null,
									})
								}
								label="Ruolo"
							/>
							{newFields.primaryRole &&
							newFields.primaryRole.value !== "Goalkeeper" ? (
								<VLFSelectNew
									options={playerRolesList.map((r) => ({
										value: r.id,
										label: r.name,
									}))}
									value={newFields.secondaryRole || null}
									onChange={(v) =>
										setNewFields({...newFields, secondaryRole: v})
									}
									label="Ruolo specifico"
								/>
							) : null}
							<VLFDatePicker
								label="Data di nascita"
								value={newFields.dateOfBirth}
								onChange={(v) => setNewFields({...newFields, dateOfBirth: v})}
								size="small"
							/>
						</>
					)}
					{!isPlayers && (
						<>
							<VLFSelectNew
								options={staffUnits.map((su) => ({
									value: su.id,
									label: su.name,
								}))}
								value={newFields.staffUnit}
								onChange={(v) => {
									setNewFieldsErrors([]);
									setNewFields({...newFields, staffUnit: v});
								}}
								label="Area"
								required
								error={newFieldsErrors.includes("staffUnit")}
								helperText={
									newFieldsErrors.includes("staffUnit") ? REQUIRED_FIELD : ""
								}
							/>
							<VLFSelectNew
								options={staffRoles.map((sr) => ({
									value: sr.id,
									label: sr.name,
								}))}
								value={newFields.staffRole}
								onChange={(v) => {
									setNewFieldsErrors([]);
									setNewFields({...newFields, staffRole: v});
								}}
								label="Ruolo"
								required
								error={newFieldsErrors.includes("staffRole")}
								helperText={
									newFieldsErrors.includes("staffRole") ? REQUIRED_FIELD : ""
								}
							/>
						</>
					)}
					<div className="main-actions-bar roster-add-modal__form-actions">
						<MainActions
							actions={[
								{
									icon: <AddIcon />,
									label: "Crea e aggiungi",
									title: "Crea e aggiungi",
									disabled: addLoading,
									onClick: handleCreateNew,
								},
							]}
						/>
					</div>
				</div>
			)}
		</div>
	);

	// ── Render ───────────────────────────────────────────────────────────────────

	return (
		<Page className="page-container scouting-roster-page">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={roster ? roster.name : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<Tabs tabs={tabs} currentTab={currentTab} setCurrentTab={setCurrentTab} />
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deleteRoster()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
			<VLFModal
				open={Boolean(addModal)}
				title={isPlayers ? "Aggiungi giocatori" : "Aggiungi staff"}
				content={addModalBody}
				close={closeAddModal}
				cancelLabel="Chiudi"
				maxWidth="md"
				paperClassName="roster-add-modal"
			/>
		</Page>
	);
};

export default ScoutingRosterPage;
